import { useDialog } from 'primevue/usedialog'
import JoinWaitlistModal from '~/components/Waitlist/JoinWaitlistModal.vue'
import { graphql } from '~/resources/graphql'
import { useRpQuery } from '~/composables/graphql'
import { useQuery } from '@tanstack/vue-query'
import { ref, type Ref } from 'vue'
import { useShowAlert } from '#imports'

export function useJoinWaitlistModal(): {
    isFetching: Ref<boolean>
    showJoinWaitlistModal: (newCourseInfo: {
        courseId: string
        facilityId: string
    }) => Promise<void>
} {
    const dialog = useDialog()
    const showAlert = useShowAlert()
    const query = useRpQuery()
    const courseInfo = ref<{ courseId: string; facilityId: string } | null>(
        null
    )
    const { isFetching, refetch } = useQuery({
        enabled: false,
        queryKey: ['JoinWaitlistModalQuery', courseInfo],
        placeholderData: previousData => previousData,
        queryFn: async () => {
            if (courseInfo.value === null) {
                return null
            }

            const response = await query(
                graphql(/* GraphQL */ `
                    query JoinWaitlistModalQuery(
                        $id: ID!
                        $input: SimpleSpokeInput!
                    ) {
                        node(id: $id) {
                            __typename
                            ... on Course {
                                ...WaitlistEnrollmentCustomerPickerCourseFragment
                                ...JoinWaitlistModalCourseFragment
                            }
                        }
                    }
                `),
                {
                    id: courseInfo.value.courseId,
                    input: {
                        facilityId: courseInfo.value.facilityId
                    }
                }
            )

            if (
                response.node === null ||
                response.node.__typename !== 'Course'
            ) {
                return null
            }

            return response.node
        }
    })

    return {
        isFetching,
        showJoinWaitlistModal: (newCourseInfo: {
            courseId: string
            facilityId: string
        }) => {
            courseInfo.value = newCourseInfo

            return new Promise(resolve => {
                refetch().then(result => {
                    if (result.error || result.data === null) {
                        showAlert(
                            'Heads Up',
                            'Whoops! Something went wrong. Please refresh the page and try again.'
                        ).then(() => {
                            resolve()
                        })
                        return
                    }

                    dialog.open(JoinWaitlistModal, {
                        props: {
                            header: 'Join Waitlist',
                            pt: {
                                root: {
                                    class: 'w-full h-max-md:h-full max-h-full !rounded-none md:h-auto md:!w-[1200px]'
                                },
                                header: {
                                    class: '!rounded-none text-xl font-semibold'
                                },
                                content: {
                                    class: '!rounded-none flex-1 '
                                }
                            },
                            ptOptions: {
                                mergeProps: true
                            },
                            modal: true
                        },
                        data: {
                            course: result.data
                        },
                        onClose: () => {
                            resolve()
                        }
                    })
                })
            })
        }
    }
}
